/**
 * Pure pathname/search → screen resolver (Task A "Routing shell"). The app has exactly three
 * screens — the audience Stage window, the guest request page, and the KJ console — and none of
 * them nest or share state beyond one join code, so a hand-rolled ~50-line resolver is simpler
 * than pulling in a routing library (plan decision: "No router, none needed").
 *
 * Kept dependency-free and side-effect-free (no `window` reads here) so it is unit-testable
 * without a DOM; `useRoute.ts` is the only place that reads `window.location`/`popstate`.
 */

export type Route =
  | { kind: 'stage' }
  | { kind: 'guest'; joinCode: string }
  | { kind: 'invite'; token: string }
  | { kind: 'signup' }
  | { kind: 'signupConfirm'; token: string }
  | { kind: 'console' };

/** MVP-278: the public self-serve sign-up page. */
export const SIGNUP_PATH = '/signup';
// MVP-278 email verification: the link the confirmation mail carries, `?token=` and all.
export const SIGNUP_CONFIRM_PATH = '/signup/confirm';

/**
 * `useStageWindow.ts` opens the Stage window at exactly `${origin}${pathname}${'?view=stage'}`
 * and must not be edited — this constant, and the `view=stage` query check below, exist to keep
 * matching that URL verbatim rather than to define it.
 */
export const STAGE_PATH = '/stage';

// Join codes are short alnum strings handed out by the session (session/services/session.js);
// 4-12 is the plan's agreed bound for "looks like a join code" vs. "not a route we know".
const GUEST_CODE_RE = /^[A-Za-z0-9]{4,12}$/;
const GUEST_PATH_RE = /^\/j\/([^/]*)$/;

// MVP-226: `/invite/<token>`, the signed-out account-redemption page. The token is 32 random bytes
// base64url-encoded (`strapi-template/src/utils/invite-token.js`), so ~43 characters of
// `[A-Za-z0-9_-]` — this only has to tell "looks like a token" from "not a route we know", exactly
// like the join-code bound above; whether the token is real is the server's answer to give, and it
// gives the same one for unknown, used and revoked.
const INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;
const INVITE_PATH_RE = /^\/invite\/([^/]*)$/;

/** Strips exactly one trailing slash so `/stage/` and `/j/L4PLRL/` resolve the same as their
 * slash-less form. The root path is left alone — stripping its only '/' would leave '', which is
 * not a valid pathname to compare against (base is '/', per task spec). */
function stripTrailingSlash(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

/**
 * Resolves a `location.pathname` + `location.search` pair to a `Route`. Order matters: the Stage
 * query flag wins even off the `/stage` path (a KJ can open `?view=stage` from any URL and still
 * get the Stage window), then the guest join-code path, else the console — everything else,
 * including a malformed `/j/...` path, is the console.
 *
 * `search` is parsed with `URLSearchParams` rather than a raw substring test so an unrelated
 * param that merely contains the text "view=stage" (e.g. `?ref=view=stage`) can't false-match.
 */
export function resolveRoute(pathname: string, search: string): Route {
  const path = stripTrailingSlash(pathname);

  if (new URLSearchParams(search).get('view') === 'stage' || path === STAGE_PATH) {
    return { kind: 'stage' };
  }

  const guestMatch = path.match(GUEST_PATH_RE);
  if (guestMatch && GUEST_CODE_RE.test(guestMatch[1])) {
    return { kind: 'guest', joinCode: guestMatch[1] };
  }

  const inviteMatch = path.match(INVITE_PATH_RE);
  if (inviteMatch && INVITE_TOKEN_RE.test(inviteMatch[1])) {
    return { kind: 'invite', token: inviteMatch[1] };
  }

  if (path === SIGNUP_PATH) return { kind: 'signup' };
  if (path === SIGNUP_CONFIRM_PATH) return { kind: 'signupConfirm', token: new URLSearchParams(search).get('token') ?? '' };

  return { kind: 'console' };
}

/** Inverse of the guest rule above — the one place that builds a `/j/<code>` URL (QR panel, share
 * links), so the encoding here and the matching regex above never drift apart. `encodeURIComponent`
 * is a no-op for any code that actually satisfies `GUEST_CODE_RE` (alnum only); it only matters if
 * a caller ever passes something looser. */
export function guestPath(joinCode: string): string {
  return `/j/${encodeURIComponent(joinCode)}`;
}
